import './styles.css'
// initialize i18n (loads local resources for now)
import '@/i18n'

import { QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import type { PostHog } from 'posthog-js'
import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'

import { restoreAuthTokenFromStorage } from '@/api/client'
import { queryClient } from '@/api/queryClient'
import { ThemeProvider } from '@/shared/components/theme-provider'
import { PWAInstallProvider } from '@/shared/hooks/usePWAInstall'
import { analytics } from '@/shared/lib/analytics'
import { env } from '@/shared/lib/env'
import { initializeErrorTracking } from '@/shared/lib/error-tracking'
import { PostHogBoundary } from '@/shared/providers/PostHogBoundary'

import reportWebVitals from './reportWebVitals.ts'
// Import the generated route tree
import { routeTree } from './routeTree.gen'

// Read version from package.json
const APP_VERSION = __APP_VERSION__ || 'dev'

// Create a new router instance
restoreAuthTokenFromStorage()
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 60_000,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Initialize global error tracking
initializeErrorTracking()

// Set release version for tracking
// This will be available after PostHog initializes
setTimeout(() => {
  analytics.setReleaseVersion(APP_VERSION)
}, 100)

// Check if PostHog is configured
const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY
const isPostHogEnabled = POSTHOG_KEY && POSTHOG_KEY.length > 0

// Warn if PostHog is not configured in production
if (!isPostHogEnabled && import.meta.env.PROD) {
  console.warn('[PostHog] Analytics disabled: VITE_PUBLIC_POSTHOG_KEY not configured')
}

// Register service worker early enough to improve repeat-visit resilience
if ('serviceWorker' in navigator) {
  const registerSW = () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        if (import.meta.env.DEV) {
          console.log('[SW] Registered successfully:', registration.scope)
        }
      })
      .catch((error) => {
        console.warn('[SW] Registration failed:', error)
      })
  }

  const scheduleRegistration = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(registerSW, { timeout: 2000 })
    } else {
      setTimeout(registerSW, 600)
    }
  }

  if (document.readyState === 'complete') {
    scheduleRegistration()
  } else {
    window.addEventListener('load', scheduleRegistration, { once: true })
  }
}

// Render the app
const rootElement = document.getElementById('app')
if (rootElement) {
  // Check if the page was prerendered (has content already)
  const isPrerendered = document.documentElement.hasAttribute('data-prerendered')
  const rawPath = window.location.pathname || '/'
  const normalizedPath = rawPath.replace(/\/$/, '') || '/'
  const prerenderedRoute =
    document.documentElement.getAttribute('data-prerendered-route')?.replace(/\/$/, '') || '/'
  const canHydrate = isPrerendered && normalizedPath === prerenderedRoute

  const AppContent = (
    <PWAInstallProvider>
      <ThemeProvider>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </HelmetProvider>
      </ThemeProvider>
    </PWAInstallProvider>
  )

  const posthogOptions = {
    api_host: env.VITE_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
    ui_host: 'https://eu.i.posthog.com',
    defaults: '2025-05-24',
    capture_exceptions: true,
    debug: import.meta.env.MODE === 'development',
    loaded: (posthog: PostHog | undefined) => {
      analytics.initialize(posthog ?? null)
      analytics.setReleaseVersion(APP_VERSION)

      if (import.meta.env.DEV) {
        console.log('[PostHog] Successfully initialized')
        console.log(
          '[PostHog] API Host:',
          env.VITE_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
        )
      }
    },
    persistence: 'localStorage+cookie',
    autocapture: {
      dom_event_allowlist: [],
      capture_copied_text: true,
    },
  }

  const FullApp = (
    <StrictMode>
      <PostHogBoundary
        enabled={Boolean(isPostHogEnabled)}
        apiKey={POSTHOG_KEY ?? ''}
        options={posthogOptions}
      >
        {AppContent}
      </PostHogBoundary>
    </StrictMode>
  )

  if (isPrerendered && !canHydrate) {
    // Clear prerendered markup for non-matching routes to avoid stale UI
    rootElement.innerHTML = ''
    document.documentElement.removeAttribute('data-prerendered')
    document.documentElement.removeAttribute('data-prerendered-route')
  }

  if (canHydrate) {
    // Hydrate prerendered content - React will attach to existing DOM
    hydrateRoot(rootElement, FullApp)
  } else {
    // Fresh render for non-prerendered pages (dev mode, etc.)
    createRoot(rootElement).render(FullApp)
  }
}

// Start measuring Web Vitals and sending to PostHog
// In development, also log to console for debugging
if (import.meta.env.DEV) {
  reportWebVitals((metric) => {
    console.log('[Web Vitals]', metric.name, metric.value, metric.rating)
  })
} else {
  reportWebVitals()
}
